import {
  HttpClient,
  HttpContext,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { inject, Injectable, OnInit } from '@angular/core';
import { SghPropertyService } from './sghProperty.service';
import {
  BehaviorSubject,
  interval,
  Observable,
  Subject,
  throwError,
} from 'rxjs';
import { environments } from '../../environments/env.production';
import { BroadcastService } from './broadcast.service';
import { SghAuth } from '../models';
import { jwtDecode } from 'jwt-decode';
import { CommonLibService } from './common-lib.service';
import { constants } from '../constants';
import { LOADER_TOKEN } from '../tokens/http-context.token';
import { ClientManagementService } from '../modules/client-management/services/client-management.service';

@Injectable({ providedIn: 'root' })
export class AuthService implements OnInit {
  stateIn!: string;
  private decodedToken: any;
  userName: any;
  private commonLibService = inject(CommonLibService);
  private currentUserSubject = new Subject<string>();
  loggedInStatus: any;
  private http = inject(HttpClient);
  private sghPropertyService = inject(SghPropertyService);
  private broadcastService = inject(BroadcastService);
  private clientManagementService = inject(ClientManagementService);

  ngOnInit() {
    this.initiateUserIdleLogout(1500, 30);
  }

  login() {
    this.buildAuthorizeUrl(this.sghPropertyService.sghAuth).then((url) =>
      window.location.assign(url),
    );
  }

  //Initiate login process
  public async buildAuthorizeUrl(sghAuth: SghAuth): Promise<string> {
    this.stateIn = this.commonLibService.generateRandomString(10);
    const code_challenge = await this.commonLibService.generateCodeChallenge();
    // change the baseUrl path from "connect/authorize" to "oauth/authorize" in production
    const baseUrl = `${sghAuth.issuer}/connect/authorize?client_id=${sghAuth.clientId}&redirect_uri=${sghAuth.loginRedirectUri}&response_type=code&state=${this.stateIn}&identityToken=&version=v2.0`;

    return !sghAuth.pkce
      ? baseUrl
      : baseUrl +
          '&code_challenge=' +
          code_challenge +
          '&code_challenge_method=S256&scope=openid profile api1 offline_access roles'; // change the code_challenge_method to 's256' in production
  }

  // Generate the oauth token
  async getTokenwithAuthCodeGrant(code: any): Promise<string[]> {
    let codeval = code.split('-')[1]; // Extract the code from the string with '=' in production
    let headers = new HttpHeaders().set(
      'Content-Type',
      'application/x-www-form-urlencoded; charset=utf-',
    );
    !this.sghPropertyService.sghAuth.pkce
      ? (headers = headers.set(
          'Authorization',
          'Basic ' +
            btoa(
              this.sghPropertyService.sghAuth.clientId +
                ':' +
                this.sghPropertyService.sghAuth.clientSecret,
            ),
        ))
      : '';

    const codeVerifier =
      sessionStorage.getItem(constants.storageKeys.codeVerifier) ?? '';
    const params = new URLSearchParams();
    params.set('grant_type', 'authorization_code');
    params.set('code', code);
    params.set(
      'redirect_uri',
      this.sghPropertyService.sghAuth.loginRedirectUri,
    );
    this.sghPropertyService.sghAuth.pkce
      ? params.set('client_id', this.sghPropertyService.sghAuth.clientId)
      : null;
    this.sghPropertyService.sghAuth.pkce
      ? params.set('code_verifier', codeVerifier)
      : null;

    return await this.http
      .post<any>(
        this.sghPropertyService.sghAuth.issuer + '/connect/token', // change the baseUrl path from "connect/token" to "oauth/token" in production
        params.toString(),
        { headers: headers },
      )
      .toPromise()
      .then(
        (data: {
          access_token: string;
          refresh_token: string;
          sId: string;
          expires_in: number;
        }) => {
          this.setValue(constants.storageKeys.accessToken, data.access_token);
          this.setValue(constants.storageKeys.refreshToken, data.refresh_token);
          this.setValue(
            constants.storageKeys.accessTokenLastUpdatedAt,
            String(new Date()),
          );
          this.setValue(constants.storageKeys.sessionId, data.sId);
          this.setValue(constants.storageKeys.expiresIn, data.expires_in);
          this.setAuthLibAttributes();
          this.fetchClientsAssociatedWithUser();
          return data;
        },
      )
      .catch((error) => {
        // this.login();
        return error;
      });
  }

  // revoke token
  revokeTokens(accessToken: any) {
    const options = {
      headers: new HttpHeaders().set('Content-Type', 'text/plain'),
      responseType: 'text' as const,
    };
    if (accessToken) {
      this.http
        .post(
          this.sghPropertyService.sghAuth.issuer +
            '/tokens/revoke/access_token' +
            '?ngsw-bypass=true',
          accessToken,
          options,
        )
        .subscribe(
          (data: any) => {},
          (error: any) => console.log(error),
        );
    }
  }
  // refresh the token
  refreshTokens() {
    const refreshToken = this.getValue('refreshToken');
    if (refreshToken) {
      const params = new URLSearchParams();
      params.append('grant_type', 'refresh_token');
      params.append('refresh_token', refreshToken);
      return this.http.post(
        this.sghPropertyService.sghAuth.issuer +
          '/oauth/token' +
          '?ngsw-bypass=true',
        params.toString(),
      );
    } else return throwError(new Error('No refresh token found!'));
  }
  // check the session state
  checkSession(): Observable<any> {
    const options = {
      params: new HttpParams()
        .set(
          'sessionId',
          sessionStorage.getItem(constants.storageKeys.sessionId)!,
        )
        .set('ngsw-bypass', true),
      responseType: 'text' as const,
      context: new HttpContext().set(LOADER_TOKEN, null),
    };
    return this.http.get(environments.checkSessionApi + '/status', options);
  }

  // get the session status using cookie
  validateSessionExist() {
    const httpOptions = {
      headers: new HttpHeaders().set('Content-Type', 'text/plain'),
      responseType: 'text' as const,
      withCredentials: true,
      observe: 'response' as 'response',
    };
    return this.http.get(
      `${environments.checkSessionApi}/auth/${this.sghPropertyService.sghAuth.clientId}`, // change the baseUrl path from "checkSessionApi" to "oauth/checkSessionApi" in production
      httpOptions,
    );
  }

  // refresh the session
  refreshSession() {
    const options = {
      params: new HttpParams()
        .set(
          'sessionId',
          sessionStorage.getItem(constants.storageKeys.sessionId)!,
        )
        .set('ngsw-bypass', true),
    };
    return this.http.get(
      this.sghPropertyService.sghAuth.issuer + '/connect/revocation',
      options,
    );
  }

  getLoggedInStatus() {
    return this.loggedInStatus;
  }
  setLoggedInStatus(loggedInStatus: any) {
    this.loggedInStatus = loggedInStatus;
  }

  // refreshSessionPeriodically
  // periodicaly check the session status to proceed SSO
  private checkSessionPeriodically() {
    // interval(55 * 1000).subscribe(async () => {
    //   (await this.checkSession()).subscribe(
    //     (responseBody) => {
    //       if (responseBody == 'SESSION NOT EXIST') {
    //         this.logout();
    //       }
    //     },
    //     (error) => {
    //       console.log(error);
    //     }
    //   );
    // });
  }

  // periodically refresh the session
  private refreshSessionPeriodically() {
    // interval(10 * 60 * 1000).subscribe(async () => {
    //   (await this.refreshSession()).subscribe(
    //     (responseBody) => {},
    //     (error) => {
    //       console.log(error);
    //     }
    //   );
    // });
  }
  // initiate logout process
  async logout() {
    // this.revokeTokens(sessionStorage.getItem('accessToken'));
    window.location.href = `${
      environments.sghAuth.issuer
    }/connect/endsession?id_token_hint=${sessionStorage.getItem(
      'id_token',
    )}&post_logout_redirect_uri=${environments.sghAuth.postLogoutRedirectUri}`;
  }

  // set auth properties
  async setAuthLibAttributes() {
    const token = this.getValue('accessToken');
    if (token) {
      this.decodedToken = jwtDecode(token);
      this.userName = this.decodedToken.user_name;
      // call checksession & refresh session periodically
      this.checkSessionPeriodically();
      this.refreshSessionPeriodically();
      this.currentUserSubject.next(this.decodedToken.user_name);
    }
    // else this.login();
  }

  getProperty(propertyName: string) {
    if (propertyName === 'data') {
      return this.getValue('associatedClients')
        ? JSON.parse(this.getValue('associatedClients'))
        : null;
    }
    if (!this.decodedToken) {
      this.setAuthLibAttributes();
    }
    return this.decodedToken ? this.decodedToken[propertyName] : '';
  }

  setValue(key: string, value: any): void {
    this.broadcastService.setItem(key, value);
  }

  getValue(key: string): any {
    return this.broadcastService.getItem(key);
  }

  removeValue(key: string): void {
    this.broadcastService.removeItem(key);
  }

  getDatafromStorage(): Observable<MessageEvent> {
    return this.broadcastService.requestStoredData();
  }

  verifyTabActive(): Observable<any> {
    return this.broadcastService.requestData();
  }

  // identify user idle time & send session expiry notification
  initiateUserIdleLogout(idleTime: number, timeout: number) {
    // // let logoutDialog: MatDialogRef<DialogBoxComponent, any>;
    // // Broadcast service implementation for fetch token attributes from session storage
    //  this.idle.setIdle(idleTime);
    //  this.idle.setTimeout(timeout);
    //  this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    //  this.idle.onInterrupt.subscribe(() => {
    // //  if (logoutDialog) {
    // //  logoutDialog.close();
    // //  }
    //  });
    //  this.idle.onIdleStart.subscribe(() => {
    // //  logoutDialog = this.dialog.open(DialogBoxComponent, {
    // //  disableClose: true,
    // //  data: {
    // //  basic: true,
    // //  type: "alert",
    // //  message: `Your session will expire in seconds.`,
    // //  subText: "Unsaved data might be lost after logged out!"
    // //  }
    // //  });
    //  });
    //  this.idle.onTimeoutWarning.subscribe((countdown: number) => {
    // //  logoutDialog.componentInstance.data.message = `Your session will expire in ${countdown} seconds.`;
    //  });
    //  this.idle.onTimeout.subscribe(() => {
    //  this.logout();
    //  });
    //  this.idle.watch();
  }
  get getRole() {
    return this.getProperty('role') ?? '';
  }

  isAdmin(): boolean {
    return this.getProperty('role')?.toLowerCase() === 'admin';
  }

  fetchClientsAssociatedWithUser() {
    const payload = {
      filter: '',
      sortBy: '',
      sortOrder: 'asc',
      pageNo: 1,
      pageSize: 10000000,
      searchText: '',
    };
    this.clientManagementService
      .fetchClientsAssociatedWithUser(payload, true)
      .subscribe({
        next: (value) => {
          this.setValue(
            'associatedClients',
            JSON.stringify(
              value.data.list.map((client) => ({
                clientId: [client.clientId],
                clientName: client.name,
              })),
            ),
          );
        },
      });
  }
}
